import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import LutromLogo from '../components/LutromLogo';
import { ArrowRight, Loader2 } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextRaw = params.get('next') || '';
  const next = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(next, { replace: true });
    } catch (err) {
      // Prefer the backend's `detail` (e.g. "Invalid credentials") over
      // axios's default "Request failed with status code 401" — the
      // latter looks like a system error to a customer trying to sign in.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (typeof detail === 'string') {
        msg = detail === 'Invalid credentials' ? 'Wrong email or password' : detail;
      } else if (status === 401) {
        msg = 'Wrong email or password';
      } else if (!err?.response) {
        msg = 'Could not reach the server. Check your connection and try again.';
      } else {
        msg = 'Sign-in failed. Please try again.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-slate-900">
        <img src="https://images.unsplash.com/photo-1581089786257-d34fe7d9bff6" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/80 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/"><LutromLogo dark /></Link>
          <div>
            <div className="industrial-divider mb-6" />
            <h2 className="font-display uppercase text-4xl mb-4">Engineering operations, simplified.</h2>
            <p className="text-slate-300 max-w-md">Sign in to access your dashboard — manage work orders, track time, and keep your team running.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8"><Link to="/"><LutromLogo /></Link></div>
          <h1 className="font-display uppercase text-3xl mb-2">Sign In</h1>
          <p className="text-slate-600 mb-8">Lutrom Engineering Operations Portal &mdash; please enter your credentials.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lutrom.co.uk" className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 lutrom-metal-btn border-0" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
          <div className="mt-6 text-sm text-slate-600 text-center">
            Need access? Contact your Lutrom representative.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
